import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useClient, useUpdateClient } from "../../hooks/useClients";
import { ClientUpdate } from "../../types/client";
import AuditInfo from "../../components/ui/AuditInfo";
import { Input, Textarea } from "../../components/ui/Input";
import { auditApi } from "../../services/api";

export default function EditClientPage() {
  const { id } = useParams<{ id: string }>();
  const clientId = Number(id);
  const navigate = useNavigate();
  const { data: client, isLoading } = useClient(clientId);
  const updateMutation = useUpdateClient();
  const [form, setForm] = useState<ClientUpdate>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (client) {
      setForm({
        name: client.name,
        client_id: client.client_id,
        description: client.description || "",
      });
    }
  }, [client]);

  const handleChange = (field: keyof ClientUpdate, value: any) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateMutation.mutateAsync({
        id: clientId,
        data: {
          name: form.name || undefined,
          client_id: form.client_id || undefined,
          description: form.description || undefined,
        },
      });
      navigate("/clients");
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || !client) {
    return <div className="py-12 text-center">Loading...</div>;
  }

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          Edit Client: {client.name}
        </h2>
        <p className="text-sm text-gray-500">Update client information</p>
      </div>

      <form
        className="bg-white shadow sm:rounded-lg p-6 space-y-4"
        onSubmit={handleSubmit}
      >
        <div>
          <Input
            label="Name"
            type="text"
            variant="rounded-md"
            value={form.name || ""}
            onChange={(e) => handleChange("name", e.target.value)}
            required
          />
        </div>
        <div>
          <Input
            label="Client ID"
            type="text"
            variant="rounded-md"
            value={form.client_id || ""}
            onChange={(e) => handleChange("client_id", e.target.value)}
          />
        </div>
        <div>
          <Textarea
            label="Description"
            variant="rounded-md"
            rows={3}
            value={form.description || ""}
            onChange={(e) => handleChange("description", e.target.value)}
          />
        </div>

        <div className="flex justify-end space-x-2">
          <button
            type="button"
            className="btn-secondary px-4 py-2 rounded-md"
            onClick={() => navigate("/clients")}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary px-4 py-2 rounded-md"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
      {client?.id ? (
        <AuditInfo
          resource="clients"
          id={client.id}
          fetch={auditApi.getLastChange}
        />
      ) : null}
    </div>
  );
}
